<script lang="ts">
import { useBenefitsStore } from '@/stores/benefits'
import { type Benefit, type Nullable, type Plan } from '@/types'
import { BenefitCodes, PlanTiers } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'

import RedeemGreggsModal from '../modals/RedeemGreggsModal.vue'
import RedeemModal from '../modals/RedeemModal.vue'

/**
 * BenefitButtons
 */
export default defineComponent({
  components: {
    RedeemGreggsModal,
    RedeemModal,
  },
  props: {
    /**
     * A configured benefit object.
     */
    benefit: {
      type: Object as PropType<Benefit>,
      required: true,
    },
    /**
     * Disables the redemption button.
     */
    disabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Fetches/redeems the benefit upon opening the modal.
     */
    claimOnOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return {
      BenefitCodes,
      benefitsStore: useBenefitsStore(),
    }
  },
  computed: {
    isAvailable(): boolean {
      return this.benefitsStore.isAvailableInPlan(this.benefit)
    },
    nextAvailablePlanForBenefit(): Nullable<Plan> {
      // Get all plans where this benefit is available
      const availablePlans = this.benefitsStore
        .benefitPlans(this.benefit)
        // First filter plans to match user's period
        .filter((plan: Plan) => {
          const userPeriod = this.$userStore.highestPlanPeriod
          // If no period, show all plans
          if (!userPeriod) {
            return true
          }
          return plan.period === userPeriod
        })
        // Then sort by tier level
        .sort((a, b) => PlanTiers[a.tier] - PlanTiers[b.tier])

      if (!availablePlans.length) {
        return null
      }

      const userTier = this.$userStore.highestPlanTier
      // If user has no subscription, return the first plan where benefit is available
      if (!userTier) {
        return availablePlans[0]
      }

      // Return the first plan that's higher than user's current tier
      const upgradePlan =
        availablePlans.find(
          (plan) => PlanTiers[plan.tier] > PlanTiers[userTier]
        ) || null

      return upgradePlan
    },
  },
})
</script>

<template>
  <template v-if="$userStore.isInTrial">
    <!-- 
      if in trial and benefit is available => end trial modal
      if in trial and benefit is NOT available => upgrade modal with "end_trial" flag
    -->
    <AppEndTrialModal
      v-if="isAvailable && $userStore.highestPlanTier !== 'lite'"
      :benefit
    />

    <AppUpgradeModal
      v-if="!isAvailable && $userStore.hasSubscription"
      :user-plan="$userStore.highestPlan"
      :upgrade-plan="nextAvailablePlanForBenefit"
    >
      <template #default="{ openModal }">
        <AppButton
          class="button-primary mx-auto"
          :title="`Upgrade to ${nextAvailablePlanForBenefit?.name} to Claim`"
          @click="openModal"
        >
          Upgrade to Claim
        </AppButton>
      </template>
    </AppUpgradeModal>
  </template>

  <template v-if="!$userStore.isInTrial">
    <template v-if="!isAvailable">
      <AppUpgradeModal
        v-if="$userStore.hasSubscription"
        :user-plan="$userStore.highestPlan"
        :upgrade-plan="nextAvailablePlanForBenefit"
      >
        <template #default="{ openModal }">
          <AppButton
            class="button-primary mx-auto min-w-full"
            :title="`Upgrade to ${nextAvailablePlanForBenefit?.name} to Claim`"
            @click="openModal"
          >
            Upgrade to Claim
          </AppButton>
        </template>
      </AppUpgradeModal>
    </template>

    <template v-else>
      <RedeemGreggsModal
        v-if="benefit.code === BenefitCodes.Greggs"
        :benefit
        :claimed-codes="$userStore.getClaimedCode(benefit)"
        :disabled="disabled"
      />
      <RedeemModal
        v-if="benefit.code !== BenefitCodes.Greggs"
        :benefit
        :claimed-codes="$userStore.getClaimedCode(benefit)"
        :disabled="disabled"
        :claim-on-open="claimOnOpen"
        :close-button-class="
          benefit.code === BenefitCodes.Tastecard
            ? 'text-black'
            : 'text-pure-white'
        "
      >
        <slot />
      </RedeemModal>
    </template>
  </template>
</template>

<style lang="scss" scoped></style>
